import {
  ApolloClient,
  ApolloLink,
  CombinedGraphQLErrors,
  from,
  HttpLink,
  InMemoryCache,
  split,
} from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';
import { RetryLink } from '@apollo/client/link/retry';
import { GraphQLWsLink } from '@apollo/client/link/subscriptions';
import { getMainDefinition } from '@apollo/client/utilities';
import fetch from 'cross-fetch';
import { CloseCode, createClient } from 'graphql-ws';
import { ApiName } from 'types/apiName';

const AUTH_FAILURE_WS_CLOSE_CODES = new Set<number>([
  CloseCode.Unauthorized,
  CloseCode.Forbidden,
  CloseCode.ConnectionAcknowledgementTimeout,
]);

const WS_CONNECTION_ACK_TIMEOUT_MS = 10_000;
const SIGNAL_AUTH_ERROR_COOLDOWN_MS = 5_000;

const getWebSocketCloseCode = (event: unknown): number | undefined => {
  if (typeof event !== 'object' || event === null || !('code' in event)) {
    return undefined;
  }

  const code = (event as { code: unknown }).code;

  return typeof code === 'number' ? code : undefined;
};

const isSubscriptionOperation = (query: any) => {
  const definition = getMainDefinition(query);
  return (
    definition.kind === 'OperationDefinition' &&
    definition.operation === 'subscription'
  );
};

export const getApolloClient = ({
  getJwtToken,
  signalAuthError,
}: {
  getJwtToken: () => Promise<string | undefined>;
  signalAuthError: () => Promise<void>;
}) => {
  const httpLinks: Partial<Record<ApiName, ApolloLink>> = {};
  const webSocketLinks: Partial<Record<ApiName, GraphQLWsLink>> = {};

  const retryLink = new RetryLink({
    delay: {
      initial: 300,
      max: Infinity,
      jitter: true,
    },
    attempts: {
      max: 5,
      retryIf: (error, operation) =>
        !!error && !isSubscriptionOperation(operation.query),
    },
  });

  let lastSignalAuthErrorAt = 0;

  const signalAuthErrorIfNecessary = async () => {
    const token = await getJwtToken();
    if (!token) {
      return;
    }

    const now = Date.now();

    if (now - lastSignalAuthErrorAt < SIGNAL_AUTH_ERROR_COOLDOWN_MS) {
      return;
    }

    lastSignalAuthErrorAt = now;

    try {
      await signalAuthError();
    } catch (error) {
      lastSignalAuthErrorAt = 0;

      // eslint-disable-next-line no-console
      console.error('signalAuthError failed:', error);
    }
  };

  const errorLink = onError((o) => {
    if (o.error instanceof CombinedGraphQLErrors) {
      for (const error of o.error.errors) {
        if (error.extensions?.code === 'FORBIDDEN') {
          void signalAuthErrorIfNecessary();
        }
      }
    }
  });

  const onWebSocketClosed = (event: unknown) => {
    const code = getWebSocketCloseCode(event);

    if (code !== undefined && AUTH_FAILURE_WS_CLOSE_CODES.has(code)) {
      // eslint-disable-next-line no-console
      console.error(`WebSocket closed with code: ${code}`);

      void signalAuthErrorIfNecessary();
    }
  };

  const getWebSocketLink = (endpoint: string) =>
    new GraphQLWsLink(
      createClient({
        url: `${endpoint}/graphql`,
        connectionAckWaitTimeout: WS_CONNECTION_ACK_TIMEOUT_MS,
        connectionParams: async () => {
          const token = await getJwtToken();

          return {
            headers: token ? { authorization: `Bearer ${token}` } : {},
          };
        },
        on: {
          closed: onWebSocketClosed,
        },
      }),
    );

  webSocketLinks[ApiName.plutoApi] = getWebSocketLink(
    import.meta.env.VITE_PLUTO_WS_ENDPOINT,
  );
  webSocketLinks[ApiName.plutoAvailabilityApi] = getWebSocketLink(
    import.meta.env.VITE_PLUTO_AVAILABILITY_WS_ENDPOINT,
  );

  const getHttpLink = (endpoint: string) =>
    new HttpLink({
      uri: `${endpoint}/graphql`,
      fetch,
    });

  httpLinks[ApiName.plutoApi] = getHttpLink(
    import.meta.env.VITE_PLUTO_API_ENDPOINT,
  );
  httpLinks[ApiName.plutoAvailabilityApi] = getHttpLink(
    import.meta.env.VITE_PLUTO_AVAILABILITY_API_ENDPOINT,
  );

  const authLink = setContext(async (_, { headers }) => {
    const token = await getJwtToken();

    return {
      headers: {
        ...headers,
        ...(token
          ? {
              authorization: `Bearer ${token}`,
            }
          : {}),
      },
    };
  });

  const finalLink = new ApolloLink((operation, forward) => {
    const apiName = operation.getContext()?.apiName as ApiName | undefined;

    if (!apiName) {
      const errorMessage = 'API name is required in operation context';

      // eslint-disable-next-line no-console
      console.error(errorMessage);

      throw new Error(errorMessage);
    }

    if (apiName === ApiName.plutoAvailabilityApi) {
      return split(
        ({ query }) => isSubscriptionOperation(query),
        webSocketLinks[ApiName.plutoAvailabilityApi]!,
        authLink.concat(httpLinks[ApiName.plutoAvailabilityApi]!),
      ).request(operation, forward);
    }

    if (apiName === ApiName.plutoApi) {
      return split(
        ({ query }) => isSubscriptionOperation(query),
        webSocketLinks[ApiName.plutoApi]!,
        authLink.concat(httpLinks[ApiName.plutoApi]!),
      ).request(operation, forward);
    }

    apiName satisfies never;

    throw new Error(`Unhandled API name: ${apiName}`);
  });

  const apolloClient = new ApolloClient({
    link: from([retryLink, errorLink, finalLink]),
    cache: new InMemoryCache({
      typePolicies: {
        Query: {
          fields: {
            // Merging is done inside subscribeToMore/fetchMore for more customisability
            // Reasoning for keyArgs: ["end"] in DOCS/graphql-apollo
            states: { keyArgs: ['end'], merge: false },
          },
        },
      },
    }),
    defaultOptions: {
      watchQuery: {
        errorPolicy: 'all',
      },
      query: {
        errorPolicy: 'all',
      },
      mutate: {
        errorPolicy: 'all',
      },
    },
  });

  let prevJwtToken: string | undefined = undefined;
  let inFlightJwtTokenPoll: Promise<void> | undefined = undefined;

  const pollJwtToken = async () => {
    const jwtToken = await getJwtToken();

    if (jwtToken !== prevJwtToken) {
      prevJwtToken = jwtToken;

      for (const webSocketLink of Object.values(webSocketLinks)) {
        webSocketLink.client.terminate();
      }

      if (!jwtToken) {
        await apolloClient.clearStore();
      }
    }
  };

  const runJwtTokenPoll = async () => {
    if (inFlightJwtTokenPoll) {
      return;
    }

    inFlightJwtTokenPoll = pollJwtToken();

    try {
      await inFlightJwtTokenPoll;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('JWT token poll failed:', error);
    } finally {
      inFlightJwtTokenPoll = undefined;
    }
  };

  inFlightJwtTokenPoll = (async () => {
    try {
      prevJwtToken = await getJwtToken();
    } finally {
      inFlightJwtTokenPoll = undefined;
    }
  })();

  const jwtTokenUpdateInterval = setInterval(async () => {
    await runJwtTokenPoll();
  }, 1000);

  const dispose = async () => {
    clearInterval(jwtTokenUpdateInterval);

    try {
      await inFlightJwtTokenPoll;
    } catch {}

    apolloClient.stop();

    for (const webSocketLink of Object.values(webSocketLinks)) {
      await webSocketLink.client.dispose();
    }

    await apolloClient.clearStore();
  };

  return { apolloClient, dispose };
};
